import {Box} from '@mui/material';
import {Redirect, Route, Switch, useParams, useRouteMatch} from 'react-router-dom';
import {ContentPage} from '../content-page/content-page';
import ProfessionalCaregiverApplicationsProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/professional-caregivers/edit/applications/professional-caregiver-applications.professional-caregiver.screen';
import ProfessionalCaregiverProfileProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/professional-caregivers/edit/profile/professional-caregiver-profile.professional-caregiver.screen';
import ProfessionalCaregiverUserCardsProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/professional-caregivers/edit/user-cards/professional-caregiver-user-cards.professional-caregiver.screen';
import ProfessionalCaregiverAssociateUserCardProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/professional-caregivers/edit/associate-user-card/professional-caregiver-associate-user-card.professional-caregiver.screen';
import CaregiverProfileSystemAdministratorOfficeScreen from '../../screens/private/office/system-administrator/caregivers/edit/profile/caregiver-profile.system-administrator.screen';
import CaregiverAffiliationsSystemAdministratorOfficeScreen from '../../screens/private/office/system-administrator/caregivers/edit/affiliations/caregiver-affiliations.system-admin.screen';
import CaregiverApplicationsSystemAdministratorOfficeScreen from '../../screens/private/office/system-administrator/caregivers/edit/applications/caregiver-applications.system-administrator.screen';
import CaregiverUserCardsSystemAdministratorOfficeScreen from '../../screens/private/office/system-administrator/caregivers/edit/user-cards/caregiver-user-cards.system-administrator.screen';
import ProfessionalCaregiverPlaylistsProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/professional-caregivers/edit/playlists/professional-caregiver-playlist.professional-caregiver.screen';
import ProfessionalCaregiverPlaylistItemsProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/professional-caregivers/edit/playlist-items/professional-caregiver-playlist-items.professional-caregiver.screen';
import CaregiverPlaylistsSystemAdministratorOfficeScreen from '../../screens/private/office/system-administrator/caregivers/edit/playlists/caregiver-playlists.system-administrator.screen';
import {useAuth} from '../../contexts/auth/context';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import UserCardProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/user-cards/edit/user-card.professional-caregiver.screen';
import {CaregiverMode} from '../../enums';
import AccessRequiredRoute from '../access-required-route';
import {useMemo} from 'react';
import PermissionsRequiredRoute from '../permissions-required-route';
import {ProfessionalCareServicesProfessionalCaregiverScreen} from '../../screens/private/office/professional-caregiver/professional-caregivers/edit/care-services/professional-care-services.professional-caregiver.screen';

export enum CaregiverPathExtensions {
  Profile = 'profile',
  Affiliations = 'affiliations',
  Applications = 'applications',
  ServiceShits = 'care-services',
  Application = 'applications/:applicationId',
  UserCards = 'user-cards',
  UserCard = 'user-cards/:id',
  AssociateUserCard = 'user-cards/associate',
  Playlists = 'playlists',
  PlaylistItems = 'playlists/:playlistId',
  PlaylistItem = 'playlists/:playlistId/items/:itemId',
}

/**
 * Caregiver navigation tabs used to navigate between the caregiver's screen.
 *
 * Permissions are given by props.
 */
export const CaregiverRoutes = ({
  caregiverMode,
}: {
  caregiverMode: CaregiverMode.SystemAdministrator | CaregiverMode.Professional;
}) => {
  const {hasWritePermission} = useAuth();

  // Get params
  let {id, entityId}: {id?: string; entityId?: string} = useParams();
  if (id?.toLowerCase() === 'invite') {
    id = undefined;
  }

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path, url} = useRouteMatch();

  const [
    canWriteTeamProfessionalApplications,
    canWriteTeamProfessionalCards,
    canWriteTeamProfessionalPlaylists,
  ] = useMemo(
    () => [
      hasWritePermission(RolePermissionType.Team_Professionals_Applications),
      hasWritePermission(RolePermissionType.Team_Professionals_Cards),
      hasWritePermission(RolePermissionType.Team_Professionals_Playlists),
    ],
    [hasWritePermission]
  );

  // Only professional caregivers that are managers or administrators can
  // associate/dissociate cards. All other professional caregivers can only view.
  return (
    <ContentPage fullWidth>
      {/* Main content */}
      <Box>
        {caregiverMode === CaregiverMode.SystemAdministrator && (
          <Switch>
            <Route exact path={`${path}/${CaregiverPathExtensions.Profile}`}>
              <Box
                sx={{
                  background: 'inherit',
                  width: '100%',
                  padding: {xs: '40px 10px 51px 10px', md: '40px 66px 51px 66px'},
                  borderRadius: '10px',
                }}
              >
                <CaregiverProfileSystemAdministratorOfficeScreen caregiverMode={caregiverMode} />
              </Box>
            </Route>

            <Route exact path={`${path}/${CaregiverPathExtensions.Affiliations}`}>
              <CaregiverAffiliationsSystemAdministratorOfficeScreen />
            </Route>

            <Route exact path={`${path}/${CaregiverPathExtensions.AssociateUserCard}`}>
              <ProfessionalCaregiverAssociateUserCardProfessionalCaregiverOfficeScreen />
            </Route>

            <Route exact path={`${path}/${CaregiverPathExtensions.Applications}`}>
              <CaregiverApplicationsSystemAdministratorOfficeScreen />
            </Route>

            <Route exact path={`${path}/${CaregiverPathExtensions.AssociateUserCard}`}>
              <ProfessionalCaregiverAssociateUserCardProfessionalCaregiverOfficeScreen />
            </Route>

            <Route exact path={`${path}/${CaregiverPathExtensions.UserCards}`}>
              <CaregiverUserCardsSystemAdministratorOfficeScreen />
            </Route>

            <Route exact path={`${path}/${CaregiverPathExtensions.Playlists}`}>
              <CaregiverPlaylistsSystemAdministratorOfficeScreen />
            </Route>

            {/** Default Route */}
            <Route path={path}>
              <Redirect to={url} />
            </Route>
          </Switch>
        )}

        {caregiverMode === CaregiverMode.Professional && (
          <Switch>
            <PermissionsRequiredRoute
              exact
              requiredPermissions={[
                {type: RolePermissionType.Team_Professionals_Profile, canRead: true},
              ]}
              path={`${path}/${CaregiverPathExtensions.Profile}`}
            >
              <ProfessionalCaregiverProfileProfessionalCaregiverOfficeScreen
                caregiverMode={caregiverMode}
              />
            </PermissionsRequiredRoute>

            <AccessRequiredRoute
              access="care_management"
              exact
              requiredPermissions={[
                {type: RolePermissionType.Team_Professionals_CareServices, canRead: true},
              ]}
              path={`${path}/${CaregiverPathExtensions.ServiceShits}`}
            >
              <ProfessionalCareServicesProfessionalCaregiverScreen />
            </AccessRequiredRoute>

            <AccessRequiredRoute
              entityId={entityId}
              access="digital_inclusion"
              exact
              requiredPermissions={[
                {type: RolePermissionType.Team_Professionals_Applications, canRead: true},
              ]}
              path={`${path}/${CaregiverPathExtensions.Applications}`}
            >
              <ProfessionalCaregiverApplicationsProfessionalCaregiverOfficeScreen
                canWrite={canWriteTeamProfessionalApplications}
              />
            </AccessRequiredRoute>

            <AccessRequiredRoute
              entityId={entityId}
              access="digital_inclusion"
              exact
              requiredPermissions={[
                {type: RolePermissionType.Team_Professionals_Cards, canRead: true},
              ]}
              path={`${path}/${CaregiverPathExtensions.UserCards}`}
            >
              <ProfessionalCaregiverUserCardsProfessionalCaregiverOfficeScreen
                canWrite={canWriteTeamProfessionalCards}
              />
            </AccessRequiredRoute>

            <AccessRequiredRoute
              entityId={entityId}
              access="digital_inclusion"
              exact
              requiredPermissions={[
                {type: RolePermissionType.Team_Professionals_Cards, canWrite: true},
              ]}
              path={`${path}/${CaregiverPathExtensions.AssociateUserCard}`}
            >
              <ProfessionalCaregiverAssociateUserCardProfessionalCaregiverOfficeScreen />
            </AccessRequiredRoute>

            <AccessRequiredRoute
              entityId={entityId}
              access="digital_inclusion"
              exact
              requiredPermissions={[
                {type: RolePermissionType.Team_Professionals_Cards, canRead: true},
              ]}
              path={`${path}/${CaregiverPathExtensions.UserCard}`}
            >
              <UserCardProfessionalCaregiverOfficeScreen canWrite={canWriteTeamProfessionalCards} />
            </AccessRequiredRoute>

            <AccessRequiredRoute
              entityId={entityId}
              access="digital_inclusion"
              exact
              requiredPermissions={[
                {type: RolePermissionType.Team_Professionals_Playlists, canRead: true},
              ]}
              path={`${path}/${CaregiverPathExtensions.Playlists}`}
            >
              <ProfessionalCaregiverPlaylistsProfessionalCaregiverOfficeScreen
                canWrite={canWriteTeamProfessionalPlaylists}
              />
            </AccessRequiredRoute>

            <AccessRequiredRoute
              entityId={entityId}
              access="digital_inclusion"
              requiredPermissions={[
                {type: RolePermissionType.Team_Professionals_Playlists, canRead: true},
              ]}
              path={`${path}/${CaregiverPathExtensions.PlaylistItems}`}
            >
              <ProfessionalCaregiverPlaylistItemsProfessionalCaregiverOfficeScreen
                canWrite={canWriteTeamProfessionalPlaylists}
              />
            </AccessRequiredRoute>

            {/** Default Route */}
            <Route path={path}>
              <Redirect to={url} />
            </Route>
          </Switch>
        )}

        {/** Default Route */}
        <Route exact path={path}>
          <Redirect to={`${url}/${CaregiverPathExtensions.Profile}`} />
        </Route>
      </Box>
    </ContentPage>
  );
};
